/* eslint-disable no-confusing-arrow */
import { takeLatest, call, put, select } from 'redux-saga/effects';

// Connected Router
import { push } from 'connected-react-router';

// Services
import { sellingTerminalsApi } from 'services/apiDigitalTicket/sellingTerminals';
import { reportsApi } from 'services/apiDigitalTicket/reports';
import { sellingPointsApi } from 'services/apiDigitalTicket/sellingPoints';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import * as UsersActions from 'store/ducks/users/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import { getBoletoUrl } from 'helpers/boleto';
import { CONST_SELLING_TERMINALS_OBJECT } from 'constants/sellingTerminals';
import PATH from 'constants/basePath';
import PAYMENT_CONSTANTS from 'constants/payments';

import * as SellingTerminalsActions from './actions';

import { SellingTerminalsTypes, SellingTerminalInterface } from './types';

function* list({
  payload,
  meta,
}: ReturnType<
  typeof SellingTerminalsActions.listSellingTerminalsRequest
>): any {
  try {
    const { body, filtering, notOperatorId } = payload;
    if (!filtering) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando redes' }));
    }
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const params = notOperatorId
      ? { ...body }
      : { transportOperatorId: transportOperator.id, ...body };
    const response = yield call(sellingTerminalsApi.get, {
      params,
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.listSellingTerminalsSuccess({
          sellingTerminalsList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      if (!filtering) yield put(DialogsActions.setClose());

      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - listSellingTerminals - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.listSellingTerminalsFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* get({
  payload,
  meta,
}: ReturnType<typeof SellingTerminalsActions.getSellingTerminalRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando rede' }));

    const { withoutTransportOperatorId } = payload;
    delete payload.withoutTransportOperatorId;

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    let response;
    if (!withoutTransportOperatorId) {
      response = yield call(sellingTerminalsApi.get, {
        params: { transportOperatorId: transportOperator.id, ...payload },
      });
    } else {
      response = yield call(sellingTerminalsApi.get, {
        params: { ...payload },
      });
    }

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.getSellingTerminalSuccess({
          sellingTerminalData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield meta.promise.resolve(response.data);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);
    console.error('sellingTerminals - getSellingTerminal - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.getSellingTerminalFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* filter({
  payload,
  meta,
}: ReturnType<
  typeof SellingTerminalsActions.filterSellingTerminalsRequest
>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.filterSellingTerminalsSuccess({
          sellingTerminalsList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - filterSellingTerminals - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.filterSellingTerminalsFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* create({
  payload,
}: ReturnType<
  typeof SellingTerminalsActions.createSellingTerminalRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando rede' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.createSellingTerminalSuccess({
          sellingTerminalData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Rede criada com sucesso',
        }),
      );
      yield put(push(PATH.SALES_NETWORK.BASE_URL));
      yield put(
        UsersActions.createPasswordRequest({ email: response.data.email }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - createSellingTerminal - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.createSellingTerminalFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<
  typeof SellingTerminalsActions.updateSellingTerminalRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando rede' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const data = (payload as { isSellingTerminal: boolean }).isSellingTerminal
      ? { ...payload.body }
      : { transportOperatorId: transportOperator.id, ...payload.body };

    const response = yield call(sellingTerminalsApi.update, {
      data,
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.updateSellingTerminalSuccess({
          sellingTerminalData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Rede alterada com sucesso',
        }),
      );
      if ((payload as { isSellingTerminal: boolean }).isSellingTerminal) {
        yield put(push(PATH.SELLING_POINTS.BASE_URL));
      } else {
        yield put(push(PATH.SALES_NETWORK.BASE_URL));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - updateSellingTerminal - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.updateSellingTerminalFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<
  typeof SellingTerminalsActions.deleteSellingTerminalRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo rede' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(SellingTerminalsActions.deleteSellingTerminalSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Rede excluída com sucesso',
        }),
      );
      yield put(
        SellingTerminalsActions.listSellingTerminalsRequest({
          body: {
            _includes: 'resume',
          },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - deleteSellingTerminal - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.deleteSellingTerminalFailure());
    yield put(DialogsActions.setClose());
  }
}

function* addPayment({
  payload,
  meta,
}:
  | ReturnType<typeof SellingTerminalsActions.addPaymentSellingTerminalRequest>
  | any): any {
  try {
    const {
      isSellingTerminal,
      withoutTransportOperatorId,
      day,
      end,
      start,
    } = payload;
    delete payload.isSellingTerminal;
    delete payload.withoutTransportOperatorId;

    yield put(
      DialogsActions.setOpenLoading({
        title: 'Realizando repasse, por favor, aguarde...',
      }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    let response;
    if (!withoutTransportOperatorId) {
      response = yield call(sellingTerminalsApi.addPayment, {
        data: { transportOperatorId: transportOperator.id, ...payload },
      });
    } else {
      response = yield call(sellingTerminalsApi.addPayment, {
        data: { ...payload },
      });
    }

    if (response.status === 200) {
      const { paymentRequestId = '' } = response.data;
      yield put(SellingTerminalsActions.addPaymentSellingTerminalSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Pagamento criado com sucesso',
        }),
      );
      // @ts-ignore
      if (payload?.payment?.type === 'BOLETO') {
        try {
          const boletoUrl = yield call(getBoletoUrl, paymentRequestId);
          if (boletoUrl) {
            window.open(
              boletoUrl,
              '_blank', // <- This is what makes it open in a new window.
            );
          }
        } catch (boletoError) {
          console.error('selling terminals - boleto url - error', boletoError);
        }
      }

      if (isSellingTerminal) {
        yield put(
          SellingTerminalsActions.listSellingShiftsRequest({
            day,
            end,
            start,
            withoutTransportOperatorId,
            _includes: 'transaction,payment,point',
            _limit: 9999,
          }),
        );
      } else {
        yield put(
          SellingTerminalsActions.listSellingTerminalsRequest({
            body: {
              end,
              start,
              day,
            },
          }),
        );
      }

      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);
    meta.promise.resolve(error.response);

    console.error(
      'sellingTerminals - addPaymentSellingTerminal - error',
      error,
    );
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.addPaymentSellingTerminalFailure());
    yield put(DialogsActions.setClose());
  }
}

function* addPaymentPrePaid({
  payload,
  meta,
}:
  | ReturnType<
      typeof SellingTerminalsActions.addPrePaidPaymentSellingTerminalRequest
    >
  | any): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Realizando recarga, por favor, aguarde...',
      }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(sellingTerminalsApi.addPaymentPrePaid, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      const { paymentRequestId = '' } = response.data;
      yield put(
        SellingTerminalsActions.addPrePaidPaymentSellingTerminalSuccess(),
      );

      // @ts-ignore
      if (payload?.payment?.type === 'BOLETO') {
        try {
          const boletoUrl = yield call(getBoletoUrl, paymentRequestId);
          if (boletoUrl) {
            window.open(
              boletoUrl,
              '_blank', // <- This is what makes it open in a new window.
            );
          }
        } catch (boletoError) {
          console.error('selling terminals - boleto url - error', boletoError);
        }
      }

      yield meta.promise.resolve(response);
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error(
      'sellingTerminals - addPaymentSellingTerminal - error',
      error,
    );
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield meta.promise.reject(error);
    yield put(
      SellingTerminalsActions.addPrePaidPaymentSellingTerminalFailure(),
    );
    yield put(DialogsActions.setClose());
  }
}

function* changeStatus({
  payload,
}: ReturnType<
  typeof SellingTerminalsActions.changeSellingTerminalStatusRequest
>): any {
  try {
    let address;
    let titleSuccess;
    let titleLoading;
    const { newStatus, sellingTerminalData } = payload;
    if (CONST_SELLING_TERMINALS_OBJECT.STATUS.ACTIVE.KEY === newStatus) {
      titleLoading = 'Ativando rede';
      titleSuccess = 'Rede ativada com sucesso';
    } else {
      titleLoading = 'Bloqueando rede';
      titleSuccess = 'Rede bloqueada com sucesso';
    }

    yield put(DialogsActions.setOpenLoading({ title: titleLoading }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    if (sellingTerminalData.Addresses && sellingTerminalData.Addresses[0]) {
      address = {
        addressId: sellingTerminalData.Addresses[0].id,
        cep: sellingTerminalData.Addresses[0].cep,
        street: sellingTerminalData.Addresses[0].street,
        number: sellingTerminalData.Addresses[0].number,
        compl: sellingTerminalData.Addresses[0].compl,
        neigh: sellingTerminalData.Addresses[0].neigh,
        city: sellingTerminalData.Addresses[0].city,
        state: sellingTerminalData.Addresses[0].state,
      };
    } else {
      address = {};
    }

    const body = {
      sellingTerminalId: sellingTerminalData.id,
      fee: sellingTerminalData.fee,
      status: newStatus,
      type: sellingTerminalData.type,
      allowedAmount: sellingTerminalData.allowedAmount,
      name: sellingTerminalData.name,
      document: sellingTerminalData.document,
      email: sellingTerminalData.email,
      phone: sellingTerminalData.phone,
      address,
    };

    const response = yield call(sellingTerminalsApi.update, {
      data: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(SellingTerminalsActions.changeSellingTerminalStatusSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: titleSuccess,
        }),
      );
      yield put(
        SellingTerminalsActions.listSellingTerminalsRequest({
          body: {
            _includes: 'resume',
          },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error(
      'sellingTerminals - changeSellingTerminalStatus - error',
      error,
    );
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.changeSellingTerminalStatusFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listPayments({
  payload,
}: ReturnType<typeof SellingTerminalsActions.listPaymentsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando repasses' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.getPayments, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.listPaymentsSuccess({
          paymentsList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - listPayments - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.listPaymentsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listWalletHistories({
  payload,
}: ReturnType<
  typeof SellingTerminalsActions.listSellingTerminalWalletHistoriesRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando histórico' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const { body, notOperatorId } = payload;
    const params = notOperatorId
      ? body
      : { ...body, transportOperatorId: transportOperator.id };
    const response = yield call(sellingTerminalsApi.getWalletHistory, {
      params,
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.listSellingTerminalWalletHistoriesSuccess({
          sellingTerminalWalletHistories: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - listWalletHistories - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(
      SellingTerminalsActions.listSellingTerminalWalletHistoriesFailure(),
    );
    yield put(DialogsActions.setClose());
  }
}

function* listWalletBalances({
  payload,
}: ReturnType<
  typeof SellingTerminalsActions.listSellingTerminalWalletBalancesRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando saldos' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const { body, notOperatorId } = payload;
    const params = notOperatorId
      ? body
      : { ...body, transportOperatorId: transportOperator.id };
    const response = yield call(sellingTerminalsApi.getWalletBalance, {
      params,
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.listSellingTerminalWalletBalancesSuccess({
          sellingTerminalWalletBalances: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - listWalletBalances - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(
      SellingTerminalsActions.listSellingTerminalWalletBalancesFailure(),
    );
    yield put(DialogsActions.setClose());
  }
}

function* apiTokenPackagePurchase({
  payload,
  meta,
}: ReturnType<
  typeof SellingTerminalsActions.apiTokenPackagePurchaseRequest
>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Processando compra de tokens' }),
    );

    const response = yield call(sellingTerminalsApi.apiTokenPackagePurchase, {
      data: payload,
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.apiTokenPackagePurchaseSuccess({
          apiTokenPackagePurchase: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - apiTokenPackagePurchase - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.apiTokenPackagePurchaseFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* apiTokenBalance({
  payload,
  meta,
}: ReturnType<typeof SellingTerminalsActions.apiTokenBalanceRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando saldo' }));

    const response = yield call(sellingTerminalsApi.apiTokenBalance, {
      params: payload,
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.apiTokenBalanceSuccess({
          apiTokenBalance: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - apiTokenBalance - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.apiTokenBalanceFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* apiTokenEnroll({
  payload,
  meta,
}: ReturnType<typeof SellingTerminalsActions.apiTokenEnrollRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Inscrevendo terminal em tokens' }),
    );

    const response = yield call(sellingTerminalsApi.apiTokenEnroll, {
      data: payload,
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.apiTokenEnrollSuccess({
          apiTokenEnroll: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - apiTokenEnroll - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.apiTokenEnrollFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* apiTokenSubscription({
  payload,
  meta,
}: ReturnType<
  typeof SellingTerminalsActions.apiTokenSubscriptionRequest
>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Carregando assinatura de tokens',
      }),
    );

    const response = yield call(sellingTerminalsApi.apiTokenSubscription, {
      params: payload,
    });
    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.apiTokenSubscriptionSuccess({
          apiTokenSubscription: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - apiTokenSubscription - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.apiTokenSubscriptionFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* apiTokenPackageList({
  payload,
  meta,
}: ReturnType<
  typeof SellingTerminalsActions.apiTokenPackageListRequest
>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Carregando pacotes de tokens',
      }),
    );

    const response = yield call(sellingTerminalsApi.apiTokenPackageList, {
      params: payload,
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.apiTokenPackageListSuccess({
          apiTokenPackageList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - apiTokenPackageList - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.apiTokenPackageListFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* apiTokenDisable({
  payload,
  meta,
}: ReturnType<typeof SellingTerminalsActions.apiTokenDisableRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Desabilitando cobrança por tokens',
      }),
    );

    const response = yield call(sellingTerminalsApi.apiTokenDisable, {
      data: payload,
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.apiTokenDisableSuccess({
          apiTokenDisable: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - apiTokenDisable - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.apiTokenDisableFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}
function* apiTokenSubscriptionAutoChargeUpdate({
  payload,
  meta,
}: ReturnType<typeof SellingTerminalsActions.apiTokenSubscriptionAutoChargeRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Habilitando/desabilitando cobrança automática',
      }),
    );

    const response = yield call(sellingTerminalsApi.updateTokenSubscriptionAutoCharge, {
      data: payload,
    });

    if (response.status === 200) {
      yield put(SellingTerminalsActions.apiTokenSubscriptionAutoChargeSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Cobrança automática alterada com sucesso',
        }),
      );
      yield put(
        SellingTerminalsActions.apiTokenSubscriptionRequest({
          sellingTerminalId: (payload as { sellingTerminalId: string })
            .sellingTerminalId,
        }),
      );
    }
    yield put(DialogsActions.setClose());
    yield meta.promise.resolve(response);
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - apiTokenSubscriptionAutoCharge - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.apiTokenSubscriptionAutoChargeFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}
function* listSellingTerminalCashier({
  payload,
}: ReturnType<
  typeof SellingTerminalsActions.listSellingTerminalCashierRequest
>): any {
  try {
    const { body, noShowLoading } = payload;
    if (!noShowLoading) {
      yield put(
        DialogsActions.setOpenLoading({ title: 'Carregando dados...' }),
      );
    }
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(reportsApi.reportList, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.listSellingTerminalCashierSuccess({
          sellingTerminalCashierList: response.data,
        }),
      );
      if (!noShowLoading) {
        yield put(DialogsActions.setClose());
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error(
      'sellingTerminals - listSellingTerminalCashier - error',
      error,
    );
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.listSellingTerminalCashierFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listTransactions({
  payload,
}: ReturnType<typeof SellingTerminalsActions.listTransactionsRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Carregando transações' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.getTransactions, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.listTransactionsSuccess({
          transactionsList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - listTransactions - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.listTransactionsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listResumeTransactions({
  payload,
}: ReturnType<
  typeof SellingTerminalsActions.listTransactionsResumeRequest
>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Carregando transações' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.listTransactionsResume, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.listTransactionsResumeSuccess({
          sellingTerminalsResumeList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - listTransactionsResume - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.listTransactionsResumeFailure());
    yield put(DialogsActions.setClose());
  }
}

function* getResume({
  payload,
  meta,
}: ReturnType<
  typeof SellingTerminalsActions.getSellingTerminalsResumeRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.get, {
      params: {
        transportOperatorId: transportOperator.id,
        _includes: 'resume',
        ...payload,
      },
    });

    if (response.status === 200) {
      const terminals: SellingTerminalInterface[] = Array.isArray(response.data)
        ? response.data
        : [response.data];

      const totalCollected = terminals.reduce(
        (acc, curr) => (curr.resume ? acc + curr.resume.transactionValue : 0),
        0,
      );

      const totalPaid = terminals.reduce(
        (acc, curr) =>
          curr.resume ? acc + curr.resume.terminalPaymentValue : 0,
        0,
      );

      const totalPending =
        parseFloat(totalCollected.toFixed(2)) -
        parseFloat(totalPaid.toFixed(2));

      const totalTicketCount = terminals.reduce(
        (acc, curr) =>
          curr.resume ? acc + curr.resume.ticketTransactionCount : 0,
        0,
      );

      const totalPurchaseCount = terminals.reduce(
        (acc, curr) =>
          curr.resume ? acc + curr.resume.purchaseTransactionCount : 0,
        0,
      );

      const data = {
        totalCollected,
        totalPaid,
        totalPending,
        totalTicketCount,
        totalPurchaseCount,
      };

      yield put(
        SellingTerminalsActions.getSellingTerminalsResumeSuccess({
          sellingTerminalsResume: data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield meta.promise.resolve({ ...response, data });
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error(
      'sellingTerminals - getSellingTerminalsResume - error',
      error,
    );
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.getSellingTerminalsResumeFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* getReportsResume({
  payload,
  meta,
}: ReturnType<
  typeof SellingTerminalsActions.getSellingReportsResumeRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.getTransactionsResume, {
      params: {
        transportOperatorId: transportOperator.id,
        ...payload,
      },
    });

    if (response.status === 200) {
      const totalCollected = response.data?.transactionValue || 0;

      const totalPaid = response.data?.terminalPaymentValue || 0;

      const totalPending =
        parseFloat(totalCollected.toFixed(2)) -
        parseFloat(totalPaid.toFixed(2));

      const totalTicketCount = response.data?.ticketTransactionCount || 0;

      const totalPurchaseCount = response.data?.purchaseTransactionCount || 0;

      const data = {
        totalCollected,
        totalPaid,
        totalPending,
        totalTicketCount,
        totalPurchaseCount,
      };

      yield put(
        SellingTerminalsActions.getSellingReportsResumeSuccess({
          sellingReportsResume: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield meta.promise.resolve({ ...response, data });
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - getSellingReportsResume - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.getSellingReportsResumeFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* payBoleto({
  payload,
}: ReturnType<
  typeof SellingTerminalsActions.sellingTerminalPayBoletoRequest
>): any {
  try {
    const formData = new FormData();
    formData.append('attachment', payload.attachment[0]);

    yield put(DialogsActions.setOpenLoading({ title: 'Aguarde..' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.payBoleto, {
      formData,
      transportOperator: transportOperator.id,
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Ordem de pagamento por boleto concluída com sucesso',
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(SellingTerminalsActions.sellingTerminalPayBoletoSuccess());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('Selling Terminals - Pay Boleto - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.sellingTerminalPayBoletoFailure());
    yield put(DialogsActions.setClose());
  }
}

function* dailyReport({
  payload,
}:
  | ReturnType<typeof SellingTerminalsActions.getDailySellingTerminalRequest>
  | any): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));

    const { withoutTransportOperatorId } = payload;
    delete payload.withoutTransportOperatorId;

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    let response;
    if (withoutTransportOperatorId) {
      response = yield call(sellingTerminalsApi.dailyReport, {
        params: { ...payload },
      });
    } else {
      response = yield call(sellingTerminalsApi.dailyReport, {
        params: { transportOperatorId: transportOperator.id, ...payload },
      });
    }

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.getDailySellingTerminalSuccess({
          sellingTerminalsDailyReports: response.data,
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employees - dailyReport - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(SellingTerminalsActions.getDailySellingTerminalFailure());
    yield put(DialogsActions.setClose());
  }
}
// Last params used to list the shifts, reused to refresh the list after a payment reversal
let lastShiftListParams: any = null;

function* removeSellingPointPayment({
  payload,
}: ReturnType<
  typeof SellingTerminalsActions.removeSellingPointPaymentRequest
>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Estornando repasse' }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(sellingPointsApi.removePayment, {
      data: {
        transportOperatorId: transportOperator?.id,
        paymentDate: Date.now(),
        ...payload,
      },
    });

    if (response.status === 200) {
      yield put(SellingTerminalsActions.removeSellingPointPaymentSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Repasse estornado com sucesso',
        }),
      );
      if (lastShiftListParams) {
        yield put(
          SellingTerminalsActions.listSellingShiftsRequest({
            ...lastShiftListParams,
          }),
        );
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingPoints - removeSellingPointPayment - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.removeSellingPointPaymentFailure());
    yield put(DialogsActions.setClose());
  }
}

function* shiftList({
  payload,
}:
  | any
  | ReturnType<typeof SellingTerminalsActions.listSellingShiftsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando pontos' }));

    lastShiftListParams = { ...payload };

    const { withoutTransportOperatorId } = payload;
    delete payload.withoutTransportOperatorId;

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    let response;
    if (!withoutTransportOperatorId) {
      response = yield call(sellingTerminalsApi.shiftList, {
        params: { transportOperatorId: transportOperator.id, ...payload },
      });
    } else {
      response = yield call(sellingTerminalsApi.shiftList, {
        params: { ...payload },
      });
    }

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.listSellingShiftsSuccess({
          sellingShiftList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingPoints - listSellingShifts - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.listSellingShiftsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listTicketCount({
  payload,
}: ReturnType<typeof SellingTerminalsActions.listTicketCountRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados...' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.ticketCountList, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.listTicketCountSuccess({
          ticketCountList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - listTicketCount - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.listTicketCountFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listSellingTerminalPaymentRequests({
  payload,
}: ReturnType<
  typeof SellingTerminalsActions.listSellingTerminalPaymentRequestsRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando faturas...' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.paymentRequestList, {
      params: {
        ...payload,
        transportOperatorId: transportOperator.id,
        type: PAYMENT_CONSTANTS.PAYMENT_REQUEST.TYPE.BILLING,
      },
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.listSellingTerminalPaymentRequestsSuccess({
          sellingTerminalPaymentRequestsList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error(
      'sellingTerminals - listSellingTerminalPaymentRequests - error',
      error,
    );
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.listSellingTerminalPaymentRequestsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* cancelSellingTerminalPaymentRequest({
  payload,
}: ReturnType<
  typeof SellingTerminalsActions.cancelSellingTerminalPaymentRequestRequest
>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Cancelando fatura...' }),
    );
    const {
      sellingTerminalPaymentRequestId,
      sellingTerminalId,
      _page,
      _limit,
    } = payload;
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.cancelPaymentRequest, {
      params: {
        sellingTerminalPaymentRequestId,
        transportOperatorId: transportOperator.id,
      },
    });

    if (response.status === 200) {
      yield put(SellingTerminalsActions.cancelSellingTerminalPaymentRequestSuccess());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Fatura cancelada com sucesso',
        }),
      );
      yield put(
        SellingTerminalsActions.listSellingTerminalPaymentRequestsRequest({
          sellingTerminalId,
          _page,
          _limit,
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error(
      'sellingTerminals - cancelSellingTerminalPaymentRequest - error',
      error,
    );
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.cancelSellingTerminalPaymentRequestFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateApiTokenContract({
  payload,
}: ReturnType<typeof SellingTerminalsActions.updateApiTokenContractRequest>): any {
  try {
    const { body } = payload;
    const formData = new FormData();
    formData.append('contractFile', body.contractFile[0]);

    yield put(DialogsActions.setOpenLoading({ title: 'Enviando contrato' }));

    const response = yield call(sellingTerminalsApi.updateApiTokenContract, {
      formData,
      sellingTerminalId: body.sellingTerminalId,
    });

    if (response.status === 200) {
      yield put(SellingTerminalsActions.updateApiTokenContractSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Contrato enviado com sucesso',
        }),
      );
      yield put(
        SellingTerminalsActions.apiTokenSubscriptionRequest({
          sellingTerminalId: (body as { sellingTerminalId: string })
            .sellingTerminalId,
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - updateApiTokenContract - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.updateApiTokenContractFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createTicketCount({
  payload,
}: ReturnType<typeof SellingTerminalsActions.createTicketCountRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando Gichê' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.ticketCountCreate, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(SellingTerminalsActions.createTicketCountSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Gichê criada com sucesso',
        }),
      );
      yield put(
        SellingTerminalsActions.listTicketCountRequest({
          sellingTerminalId: (payload as { sellingTerminalId: string })
            .sellingTerminalId,
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - createTicketCount - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.createTicketCountFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateTicketCount({
  payload,
}: ReturnType<typeof SellingTerminalsActions.updateTicketCountRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Atualizando Gichê...' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.ticketCountUpdate, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(SellingTerminalsActions.updateTicketCountSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Gichê atualizado com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - updateTicketCount - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.updateTicketCountFailure());
    yield put(DialogsActions.setClose());
  }
}

function* deleteTicketCount({
  payload,
}: ReturnType<typeof SellingTerminalsActions.deleteTicketCountRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Apagando Gichê...' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.ticketCountDelete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(SellingTerminalsActions.deleteTicketCountSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Gichê apagado com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - deleteTicketCount - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.deleteTicketCountFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listShiftSchedule({
  payload,
}: ReturnType<typeof SellingTerminalsActions.listShiftScheduleRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados...' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.shiftScheduleList, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.listShiftScheduleSuccess({
          shiftScheduleList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - listShiftSchedule - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.listShiftScheduleFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createShiftSchedule({
  payload,
  meta,
}: ReturnType<typeof SellingTerminalsActions.createShiftScheduleRequest>): any {
  try {
    const startedAt = payload?.startedAt;
    startedAt && delete payload?.startedAt;

    yield put(DialogsActions.setOpenLoading({ title: 'Criando escala....' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.shiftScheduleCreate, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(SellingTerminalsActions.createShiftScheduleSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Escala criada com sucesso',
        }),
      );
      yield meta.promise.resolve({ ...response });

      yield put(
        SellingTerminalsActions.listShiftScheduleRequest({
          sellingTerminalId: (payload as { sellingTerminalId: string })
            .sellingTerminalId,
          _includes: 'shift',
          date: startedAt
            ? new Date(new Date(startedAt!).setHours(0, 0, 0, 0)).getTime()
            : undefined,
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - createShiftSchedule - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.createShiftScheduleFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* updateShiftSchedule({
  payload,
  meta,
}: ReturnType<typeof SellingTerminalsActions.updateShiftScheduleRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Atualizando escala...' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.shiftScheduleUpdate, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(SellingTerminalsActions.updateShiftScheduleSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Escala atualizada com sucesso',
        }),
      );
      yield meta.promise.resolve({ ...response });
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - updateShiftSchedule - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.updateShiftScheduleFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* deleteShiftSchedule({
  payload,
  meta,
}: ReturnType<typeof SellingTerminalsActions.deleteShiftScheduleRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Apagando escala...' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.shiftScheduleDelete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(SellingTerminalsActions.deleteShiftScheduleSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Escala apagada com sucesso',
        }),
      );
      yield meta.promise.resolve({ ...response });
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - deleteShiftSchedule - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.deleteShiftScheduleFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* shiftScheduleRun({
  payload,
}: ReturnType<typeof SellingTerminalsActions.shiftScheduleRunRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Efetuando operação....' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.shiftScheduleRun, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(SellingTerminalsActions.shiftScheduleRunSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Operação efetuada com sucesso',
        }),
      );

      yield put(
        SellingTerminalsActions.listShiftScheduleRequest({
          sellingTerminalId: (payload as { sellingTerminalId: string })
            .sellingTerminalId,
          _includes: 'shift',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - shiftScheduleRun - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.shiftScheduleRunFailure());
    yield put(DialogsActions.setClose());
  }
}

function* getSellingSettings({
  payload,
}: ReturnType<typeof SellingTerminalsActions.getSellingSettingsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando configurações' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.getSellingSettings, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.getSellingSettingsSuccess({
          sellingSettingsData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - getSellingSettings - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.getSellingSettingsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateSellingSettings({
  payload,
}: ReturnType<typeof SellingTerminalsActions.updateSellingSettingsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Salvando configurações' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(sellingTerminalsApi.updateSellingSettings, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SellingTerminalsActions.updateSellingSettingsSuccess({
          sellingSettingsData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Configurações salvas com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('sellingTerminals - updateSellingSettings - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SellingTerminalsActions.updateSellingSettingsFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const sellingTerminalsSagas = [
  takeLatest(SellingTerminalsTypes.LIST_SELLING_TERMINALS_REQUEST, list),
  takeLatest(SellingTerminalsTypes.GET_SELLING_TERMINAL_REQUEST, get),
  takeLatest(SellingTerminalsTypes.FILTER_SELLING_TERMINALS_REQUEST, filter),
  takeLatest(SellingTerminalsTypes.CREATE_SELLING_TERMINAL_REQUEST, create),
  takeLatest(SellingTerminalsTypes.UPDATE_SELLING_TERMINAL_REQUEST, update),
  takeLatest(SellingTerminalsTypes.DELETE_SELLING_TERMINAL_REQUEST, remove),
  takeLatest(
    SellingTerminalsTypes.ADD_PAYMENT_SELLING_TERMINAL_REQUEST,
    addPayment,
  ),
  takeLatest(
    SellingTerminalsTypes.REMOVE_SELLING_POINT_PAYMENT_REQUEST,
    removeSellingPointPayment,
  ),
  takeLatest(
    SellingTerminalsTypes.ADD_PRE_PAID_PAYMENT_SELLING_TERMINAL_REQUEST,
    addPaymentPrePaid,
  ),
  takeLatest(
    SellingTerminalsTypes.CHANGE_SELLING_TERMINAL_STATUS_REQUEST,
    changeStatus,
  ),
  takeLatest(SellingTerminalsTypes.LIST_PAYMENTS_REQUEST, listPayments),
  takeLatest(
    SellingTerminalsTypes.LIST_SELLING_TERMINAL_WALLET_HISTORIES_REQUEST,
    listWalletHistories,
  ),
  takeLatest(
    SellingTerminalsTypes.LIST_SELLING_TERMINAL_WALLET_BALANCE_REQUEST,
    listWalletBalances,
  ),
  takeLatest(
    SellingTerminalsTypes.API_TOKEN_PACKAGE_PURCHASE_REQUEST,
    apiTokenPackagePurchase,
  ),
  takeLatest(SellingTerminalsTypes.API_TOKEN_BALANCE_REQUEST, apiTokenBalance),
  takeLatest(SellingTerminalsTypes.API_TOKEN_ENROLL_REQUEST, apiTokenEnroll),
  takeLatest(
    SellingTerminalsTypes.API_TOKEN_SUBSCRIPTION_REQUEST,
    apiTokenSubscription,
  ),
  takeLatest(
    SellingTerminalsTypes.API_TOKEN_PACKAGE_LIST_REQUEST,
    apiTokenPackageList,
  ),
  takeLatest(SellingTerminalsTypes.API_TOKEN_DISABLE_REQUEST, apiTokenDisable),
  takeLatest(SellingTerminalsTypes.API_TOKEN_SUBSCRIPTION_AUTO_CHARGE_REQUEST, apiTokenSubscriptionAutoChargeUpdate),
  takeLatest(SellingTerminalsTypes.LIST_TRANSACTIONS_REQUEST, listTransactions),
  takeLatest(
    SellingTerminalsTypes.GET_SELLING_TERMINALS_RESUME_REQUEST,
    getResume,
  ),
  takeLatest(
    SellingTerminalsTypes.GET_SELLING_REPORTS_RESUME_REQUEST,
    getReportsResume,
  ),
  takeLatest(
    SellingTerminalsTypes.LIST_TRANSACTIONS_RESUME_REQUEST,
    listResumeTransactions,
  ),
  takeLatest(
    SellingTerminalsTypes.SELLING_TERMINALS_PAY_BOLETO_REQUEST,
    payBoleto,
  ),
  takeLatest(
    SellingTerminalsTypes.GET_DAILY_SELLING_TERMINAL_REQUEST,
    dailyReport,
  ),
  takeLatest(
    SellingTerminalsTypes.LIST_SELLING_TERMINAL_CASHIER_REQUEST,
    listSellingTerminalCashier,
  ),
  takeLatest(SellingTerminalsTypes.LIST_SELLING_SHIFT_REQUEST, shiftList),
  takeLatest(SellingTerminalsTypes.LIST_TICKET_COUNT_REQUEST, listTicketCount),
  takeLatest(
    SellingTerminalsTypes.UPDATE_API_TOKEN_CONTRACT_REQUEST,
    updateApiTokenContract,
  ),
  takeLatest(
    SellingTerminalsTypes.CREATE_TICKET_COUNT_REQUEST,
    createTicketCount,
  ),
  takeLatest(
    SellingTerminalsTypes.UPDATE_TICKET_COUNT_REQUEST,
    updateTicketCount,
  ),
  takeLatest(
    SellingTerminalsTypes.DELETE_TICKET_COUNT_REQUEST,
    deleteTicketCount,
  ),
  takeLatest(
    SellingTerminalsTypes.LIST_SHIFT_SCHEDULE_REQUEST,
    listShiftSchedule,
  ),
  takeLatest(
    SellingTerminalsTypes.CREATE_SHIFT_SCHEDULE_REQUEST,
    createShiftSchedule,
  ),
  takeLatest(
    SellingTerminalsTypes.UPDATE_SHIFT_SCHEDULE_REQUEST,
    updateShiftSchedule,
  ),
  takeLatest(
    SellingTerminalsTypes.DELETE_SHIFT_SCHEDULE_REQUEST,
    deleteShiftSchedule,
  ),
  takeLatest(
    SellingTerminalsTypes.SHIFT_SCHEDULE_RUN_REQUEST,
    shiftScheduleRun,
  ),
  takeLatest(
    SellingTerminalsTypes.GET_SELLING_SETTINGS_REQUEST,
    getSellingSettings,
  ),
  takeLatest(
    SellingTerminalsTypes.UPDATE_SELLING_SETTINGS_REQUEST,
    updateSellingSettings,
  ),
  takeLatest(
    SellingTerminalsTypes.LIST_SELLING_TERMINAL_PAYMENT_REQUESTS_REQUEST,
    listSellingTerminalPaymentRequests,
  ),
  takeLatest(
    SellingTerminalsTypes.CANCEL_SELLING_TERMINAL_PAYMENT_REQUEST_REQUEST,
    cancelSellingTerminalPaymentRequest,
  ),
];
